/**
 * Bridge nativo per l'app Drive incapsulata in WebView (Android WebView / iOS WKWebView).
 * Stesso schema di Family (window.FamilyApp + user agent "AffidyaFamily(Android|IOS)/"):
 *  - user agent con token  "AffidyaDriveAndroid/<ver>"  oppure  "AffidyaDriveIOS/<ver>";
 *  - oggetto globale window.DriveApp con metodi opzionali (Android: addJavascriptInterface,
 *    iOS: user script che inoltra a webkit.messageHandlers.drive).
 * Ogni metodo è opzionale: si controlla sempre `typeof === "function"`.
 */
export const DRIVE_UA_RE = /AffidyaDrive(Android|IOS)\//;

export function isNativeDriveApp(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  return DRIVE_UA_RE.test(ua || "");
}

export function nativeDrivePlatform(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  const m = (ua || "").match(DRIVE_UA_RE);
  return m ? (m[1] === "IOS" ? "ios" : "android") : null;
}

export function getDriveBridge() {
  if (typeof window === "undefined") return null;
  const b = window.DriveApp;
  return b && typeof b === "object" ? b : null;
}

export function hasBridgeMethod(name) {
  const b = getDriveBridge();
  return !!b && typeof b[name] === "function";
}

/** WebView generica (anche senza nostro wrapper): utile per evitare popup/finestre. */
export function isLikelyWebView(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  if (isNativeDriveApp(ua)) return true;
  if (/; wv\)/.test(ua)) return true; // Android WebView
  return /(iPhone|iPad|iPod).*AppleWebKit(?!.*Safari)/.test(ua); // WKWebView senza "Safari"
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return !!mq || window.navigator?.standalone === true;
}

/**
 * Apre un link esterno (navigatore, telefono) senza popup né nuove finestre:
 *  1. app nativa con bridge -> DriveApp.openExternal(url) (intent / UIApplication.open)
 *  2. WebView o PWA installata -> navigazione top-level (il sistema passa all'app esterna)
 *  3. browser normale -> link con target _blank + noopener avviato dall'utente.
 */
export function openExternal(url) {
  if (!url) return "none";
  const b = getDriveBridge();
  if (b && typeof b.openExternal === "function") {
    b.openExternal(String(url));
    return "bridge";
  }
  if (/^(tel|mailto|sms|geo):/i.test(url) || isLikelyWebView() || isStandaloneDisplay()) {
    window.location.assign(url);
    return "location";
  }
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return "anchor";
}

export function driveMapsUrl(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}&travelmode=driving`;
}

/** Feedback aptico opzionale (bridge o Vibration API); silenzioso se non disponibile. */
export function haptic(kind = "light") {
  const b = getDriveBridge();
  if (b && typeof b.haptic === "function") {
    b.haptic(kind);
    return;
  }
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(kind === "success" ? [10, 40, 10] : 10);
  } catch {
    /* ignore */
  }
}
